/**
 * Lista de actividades disponibles para estudiantes
 * Filtrada automáticamente por classroom_id del estudiante
 */

import React, { useState, useEffect } from 'react';
import { teacherActivityService } from '../../services/teacherActivityService';
import { TeacherActivity } from '../../types';
import { authService } from '../../services/authService';
import configService from '../../services/configService';
import { useScreenOrientation } from '../../hooks/useScreenOrientation';
import './StudentActivityList.css';

interface StudentActivityListProps {
  onSelectActivity: (activity: TeacherActivity) => void;
  onClose?: () => void;
}

const StudentActivityList: React.FC<StudentActivityListProps> = ({
  onSelectActivity,
  onClose,
}) => {
  const [activities, setActivities] = useState<TeacherActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const orientation = useScreenOrientation();

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    setLoading(true);
    try {
      // SIEMPRE obtener classroom_ids directamente de Apolearn (no usar caché)
      const config = configService.getConfig();
      const email = config.email;
      const userId = config.userID;
      
      if (!email) {
        console.error('[StudentActivityList] ❌ No hay email en config');
        setActivities([]);
        return;
      }

      console.log('[StudentActivityList] 🔄 Obteniendo classroom_ids directamente de Apolearn...');
      
      // Obtener información completa del estudiante desde Apolearn
      // Esto siempre obtiene los datos frescos de la API y construye userDataComplete
      const userDataComplete = await authService.getStudentCompleteInfo({
        email: email,
        user_id: userId && userId > 0 ? userId : undefined
      });

      // Extraer classroom_ids directamente de la estructura unificada
      // userDataComplete ya contiene classroom_ids extraídos de Apolearn
      const classroomIds: number[] = userDataComplete?.user_info?.classroom_ids || [];
      
      console.log('[StudentActivityList] 📚 Classroom IDs obtenidos desde Apolearn:', {
        totalClassrooms: userDataComplete?.user_info?.classrooms?.length || 0,
        classroomIds: classroomIds,
        classrooms: userDataComplete?.user_info?.classrooms?.map((c: any) => ({
          id: c.id,
          name: c.name || 'Sin nombre'
        })) || []
      });

      if (classroomIds.length === 0) {
        console.warn('[StudentActivityList] ⚠️ No se encontraron classrooms en la respuesta de Apolearn');
      }

      // Obtener actividades disponibles (filtradas por classroom_id)
      console.log('[StudentActivityList] 🔍 Buscando actividades con classroom_ids:', classroomIds);
      const studentActivities = await teacherActivityService.getStudentActivities(classroomIds);
      
      console.log('[StudentActivityList] ✅ Actividades encontradas:', {
        total: studentActivities.length,
        activities: studentActivities.map((a: any) => ({
          id: a.id,
          classroom_id: a.classroom_id,
          task_description: a.task_description?.substring(0, 50) + '...'
        }))
      });
      
      setActivities(studentActivities);
    } catch (error) {
      console.error('[StudentActivityList] ❌ Error al cargar actividades:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={`student-activity-list ${orientation}`}>
      <div className="student-activity-list-content">
        {orientation === 'portrait' && onClose && (
          <div className="student-activity-list-header-portrait">
            <div className="student-activity-list-header-portrait-top">
              <h2>Actividades Disponibles</h2>
              <button
                className="student-activity-list-close-button"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <img 
                  src="/assets/ui/botones/Boton_Cerrar.png" 
                  alt="Cerrar"
                  className="student-activity-list-close-icon"
                />
              </button>
            </div>
          </div>
        )}

        <div className="student-activity-list-header">
          <div className="student-activity-list-header-top">
            <h2>Actividades Disponibles</h2>
            {orientation === 'landscape' && onClose && (
              <button
                className="student-activity-list-close-button-landscape"
                onClick={onClose}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <img 
                  src="/assets/ui/botones/Boton_Cerrar.png" 
                  alt="Cerrar"
                  className="student-activity-list-close-icon"
                />
              </button>
            )}
          </div>
        </div>

        <div className="student-activity-list-body">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-message">Cargando actividades...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="empty-state">
            <p>No hay actividades disponibles en este momento.</p>
            <p className="empty-hint">
              Las actividades aparecerán aquí cuando tu profesor las comparta contigo.
            </p>
          </div>
        ) : (
          <div className="activities-grid">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="activity-card"
                onClick={() => onSelectActivity(activity)}
              >
                <div className="activity-card-header">
                  {activity.classroom_id && (
                    <span className="classroom-badge">Aula: {activity.classroom_id}</span>
                  )}
                  {!activity.classroom_id && (
                    <span className="general-badge">Actividad General</span>
                  )}
                  {activity.level && (
                    <span className="level-badge">Nivel: {activity.level}</span>
                  )}
                </div>
                <div className="activity-card-body">
                  <h3 className="activity-title">Actividad</h3>
                  <p className="activity-description">
                    {truncateText(activity.task_description)}
                  </p>
                  <p className="activity-objectives-count">
                    {activity.objectives.length} objetivo{activity.objectives.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="activity-card-footer">
                  <button className="view-activity-button">Ver Actividad</button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default StudentActivityList;
